import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  DataRefsByKey,
  Domain,
  LocalizedKeyValue,
  MarketLocalizedApplianceStatus,
  Appliance,
  ApplianceCapacity,
} from '@shared/models/dataref.model';

import { AppliancesListByDomainMarket, UIDomainConfigurationWithCapacity } from '../../models/client.model';

@Component({
  selector: 'app-domains-activation-form',
  templateUrl: './domains-activation-form.component.html',
  styleUrls: ['./domains-activation-form.component.scss'],
  standalone: false,
})
export class DomainsActivationFormComponent {
  @Input() domainsByKey: DataRefsByKey<Domain> = {};
  @Input() appliancesListByDomainMarket: AppliancesListByDomainMarket = {};
  @Input() currentMarketKey = '';
  @Input() domainsWithApplianceCapacityList: UIDomainConfigurationWithCapacity[] = [];
  @Input() unitsList: LocalizedKeyValue[] = [];

  @Output() modelChanged: EventEmitter<void> = new EventEmitter();

  readonly SERVICES_TRANSLATION_KEY_PREFIX = 'APPLICATIONS.DOMAINS_SERVICES_ACTIVATION.';

  readonly SERVICES_TRANSLATION_KEYS = {
    LABEL_UGC: this.SERVICES_TRANSLATION_KEY_PREFIX + 'CHECKBOX_LABEL_UGC',
    LABEL_PERSO_ADAPTATION: this.SERVICES_TRANSLATION_KEY_PREFIX + 'CHECKBOX_LABEL_PERSONALIZED_ADAPTATION',
    CHECKBOX_TOOLTIP_UGC: this.SERVICES_TRANSLATION_KEY_PREFIX + 'CHECKBOX_TOOLTIP_UGC',
    CHECKBOX_TOOLTIP_PERSO_ADAPTATION: this.SERVICES_TRANSLATION_KEY_PREFIX + 'CHECKBOX_TOOLTIP_PERSONALIZED_ADAPTATION',
  };

  /**
   * Property used to verify if there is an activated domain which appliance has a capacity.
   * Used in the template to know if we must display an empty input to keep vertical alignments for all checkboxes
   */
  get hasSomeApplianceWithCapacityActivated(): boolean {
    return (
      this.domainsWithApplianceCapacityList.findIndex(
        (domainWithAppliance) => domainWithAppliance.isActive && domainWithAppliance.capacity !== undefined,
      ) !== -1
    );
  }

  getDefaultApplianceCapacities(
    applianceId: string | null | undefined,
    domainWithAppliance: UIDomainConfigurationWithCapacity,
  ): ApplianceCapacity[] | null {
    if (!applianceId) {
      return null;
    }

    const appliancesListOfDomainMarket = this.appliancesListByDomainMarket[domainWithAppliance.domainKey]?.[this.currentMarketKey] || [];

    const appliance = this.findApplianceInList(appliancesListOfDomainMarket, applianceId);
    if (appliance?.capacities) {
      this.initCapacityForDomainWithAppliance(domainWithAppliance);
      return appliance.capacities;
    }
    return null;
  }

  getUnitLabel(unitKey: string): string {
    const unit = this.unitsList.find((_unit: LocalizedKeyValue) => _unit.key === unitKey);
    if (unit) {
      return unit.translatedName ?? '';
    }
    return '';
  }

  getDefaultApplianceStatuts(domainKey: string, applianceKey: string | null | undefined): string {
    if (!applianceKey) {
      return '';
    }
    const appliancesListOfDomainMarket = this.appliancesListByDomainMarket[domainKey]?.[this.currentMarketKey] ?? [];

    const appliance = this.findApplianceInList(appliancesListOfDomainMarket, applianceKey);
    if (appliance) {
      const detail = appliance.localizedStatuses.find(
        (localizedStatus: MarketLocalizedApplianceStatus) => localizedStatus.market === this.currentMarketKey,
      );
      if (detail) {
        return detail.status;
      }
    }
    return '';
  }

  toggleDefaultAppliance(
    uiDomainConfiguration: UIDomainConfigurationWithCapacity,
    checkboxChangeEvent: MatCheckboxChange,
    domainConfigurationIndex: number,
  ): void {
    uiDomainConfiguration.isActive = checkboxChangeEvent.checked;
    // RG if the domain is active and without order we update it with his index
    if (uiDomainConfiguration.isActive && uiDomainConfiguration.order === undefined) {
      uiDomainConfiguration.order = domainConfigurationIndex;
    }
    if (!uiDomainConfiguration.isActive) {
      uiDomainConfiguration.defaultApplianceId = null;
      uiDomainConfiguration.isPersonalizedAdaptationEnabled = false;
      uiDomainConfiguration.isUgcEnabled = false;
      uiDomainConfiguration.capacity = undefined;
      uiDomainConfiguration.order = undefined;
    }
    this.modelChanged.emit();
  }

  updateOrderOfDomainsWithApplianceCapacity(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.domainsWithApplianceCapacityList, event.previousIndex, event.currentIndex);
    this.domainsWithApplianceCapacityList
      .filter((domain) => domain.isActive)
      .forEach((currentDomainConfiguration, index) => {
        if (currentDomainConfiguration.order !== undefined) {
          currentDomainConfiguration.order = index;
        }
      });
  }

  private initCapacityForDomainWithAppliance(domainWithAppliance: UIDomainConfigurationWithCapacity): void {
    domainWithAppliance.capacity ??= {};
  }

  private findApplianceInList(appliances: Appliance[], applianceToFindId: string): Appliance | undefined {
    return appliances.find((appliance: Appliance) => appliance.key === applianceToFindId);
  }
}
